import { type RefObject, useEffect, useState } from 'react';

/**
 * Which edges of a horizontal scroller still hide content, in the reading direction: `start` once
 * the row has scrolled away from its start, `end` while more lies past its end. The sliders fade
 * only those edges (`data-fade-start` / `data-fade-end`).
 */
export function useScrollEdges(ref: RefObject<HTMLElement | null>): {
  readonly end: boolean;
  readonly start: boolean;
} {
  const [edges, setEdges] = useState({ end: false, start: false });

  useEffect(() => {
    const slider = ref.current;
    if (slider === null) {
      return undefined;
    }
    const update = () => {
      // RTL scroll positions are negative in current browsers; the distance is what matters.
      const travelled = Math.abs(slider.scrollLeft);
      const room = slider.scrollWidth - slider.clientWidth;
      const next = { end: room - travelled > 1, start: travelled > 1 };
      setEdges((previous) =>
        previous.end === next.end && previous.start === next.start ? previous : next,
      );
    };
    update();
    slider.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(slider);
    return () => {
      slider.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [ref]);

  return edges;
}
